import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import Card from '../common/Card';
import Skeleton from '../common/Skeleton';
import GradeStamp from '../common/GradeStamp';
import { computeDraftGrade, LetterGrade } from '../../utils/gradeEngine';
import { LeagueDraftAnalytics, V1Standings } from '../../types';

interface BestDraftersContainerProps {
  leagueId: number;
  draftAnalytics?: LeagueDraftAnalytics | null;
  standings?: V1Standings | null;
  loading?: boolean;
  onTeamSelect?: (teamId: number) => void;
  className?: string;
}

interface TeamRanking {
  team_id: number;
  team_name: string;
  rank: number;
  draft_efficiency: number;
  total_value_score: number;
  best_pick_value: number;
  starter_points_from_draft: number;
  draft_grade: LetterGrade;
}

const BestDraftersContainer: React.FC<BestDraftersContainerProps> = ({
  leagueId,
  draftAnalytics,
  standings,
  loading: parentLoading = false,
  onTeamSelect,
  className = ""
}) => {
  const [rankings, setRankings] = useState<TeamRanking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  useEffect(() => {
    if (draftAnalytics) {
      try {
        // Transform to rankings with position numbers
        const teamRankings: TeamRanking[] = draftAnalytics.teams.map((team, index) => ({
          team_id: team.team_id,
          team_name: team.team_name,
          rank: index + 1,
          draft_efficiency: team.draft_performance.draft_efficiency,
          total_value_score: team.draft_performance.total_value_score,
          best_pick_value: team.best_picks[0]?.value_score || 0,
          starter_points_from_draft: team.draft_performance.starter_points_from_draft || 0,
          draft_grade: computeDraftGrade(team.draft_performance.avg_value_score)
        }));

        setRankings(teamRankings);
        setError(null);
      } catch (err) {
        setError('Failed to process draft analytics');
        console.error('Draft analytics processing error:', err);
      }
    }
  }, [draftAnalytics]);

  const getFinalRankLabel = (teamId: number): string | null => {
    if (!standings) return null;

    // Find this team in standings to get their final rank
    const teamStanding = standings.standings.find(s => s.team_id === teamId);
    if (!teamStanding) return null;

    // Champion / runner-up markers as text labels (no emoji per DESIGN.md)
    if (teamStanding.rank === 1) return 'CHAMP';
    if (teamStanding.rank === 2) return '2ND';
    if (teamStanding.rank === 3) return '3RD';
    return null;
  };

  // Performance encoded as a 2px top rail, not a full-card wash (DESIGN.md §4)
  const getPerformanceRail = (efficiency: number): string => {
    if (efficiency >= 0.8) return 'border-t-performance-elite';
    if (efficiency >= 0.6) return 'border-t-performance-good';
    if (efficiency >= 0.4) return 'border-t-performance-poor';
    return 'border-t-performance-bust';
  };

  const handleTeamClick = (teamId: number) => {
    setSelectedTeamId(teamId);
    onTeamSelect?.(teamId);
  };

  // Check if this is being used inline (no card wrapper needed)
  const isInline = className?.includes('border-0') || className?.includes('bg-transparent');

  if (parentLoading || !draftAnalytics) {
    const loadingContent = (
      <div>
        <Skeleton height="h-8" width="w-48" className="mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <Skeleton key={i} height="h-16" width="w-full" />
          ))}
        </div>
      </div>
    );
    
    return isInline ? loadingContent : (
      <Card className={`p-6 bg-background-secondary border-fieldline ${className}`}>
        {loadingContent}
      </Card>
    );
  }

  if (error) {
    const errorContent = (
      <div className="text-center text-text-secondary">
        <p>{error}</p>
      </div>
    );
    
    return isInline ? errorContent : (
      <Card className={`p-6 bg-background-secondary border-fieldline ${className}`}>
        {errorContent}
      </Card>
    );
  }

  const content = (
    <div className={`space-y-4 ${isInline ? className : ''}`}>
      {/* Header - only show if not inline */}
      {!isInline && (
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-2 font-sans">
            <Trophy className="w-5 h-5 text-accent-primary" />
            Best Drafters
          </h3>
          <div className="text-sm text-text-secondary font-sans">
            Ranked by Drafted Starters Points Scored
          </div>
        </div>
      )}

      {/* Team Rankings Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {rankings.map((team) => (
          <button
            key={team.team_id}
            onClick={() => handleTeamClick(team.team_id)}
            className={`p-2 rounded-lg bg-background-secondary border border-fieldline border-t-2 transition-all duration-200 hover:bg-background-hover ${
              getPerformanceRail(team.draft_efficiency)
            } ${
              selectedTeamId === team.team_id
                ? 'ring-2 ring-accent-primary'
                : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <GradeStamp grade={team.draft_grade} size="xs" className="flex-shrink-0" />
              <div className="min-w-0 flex-1 text-left space-y-0.5">
                {/* Rank and final-standing label */}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold font-sans text-text-primary">#{team.rank}</span>
                  {getFinalRankLabel(team.team_id) && (
                    <span className="text-[10px] font-mono tracking-wider text-accent-primary">
                      {getFinalRankLabel(team.team_id)}
                    </span>
                  )}
                </div>

                {/* Team Name */}
                <div className="font-medium text-xs text-text-primary truncate font-sans">
                  {team.team_name}
                </div>

                {/* Starter Points */}
                <div className="text-xs font-mono text-text-secondary truncate">
                  {team.starter_points_from_draft.toFixed(0)} starter pts
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="text-xs text-text-secondary text-center pt-2 border-t border-fieldline font-sans">
        Based on starter points from drafted players only
      </div>
    </div>
  );
  
  // Return with or without Card wrapper
  return isInline ? content : (
    <Card className={`p-6 bg-background-secondary border-fieldline ${className}`}>
      {content}
    </Card>
  );
};

export default BestDraftersContainer;